import { z } from 'zod'

import {
  bytesSchema,
  idSchema,
  invoiceMethodSchema,
  isoInstantSchema,
  noteSchema,
  planCodeSchema,
  platformRoleSchema,
  tenantStatusSchema,
  usernameSchema,
} from '../common'
import { passwordSchema } from '../auth/auth'

/**
 * P17–P19 · Platform settings, team and broadcast (screen 11).
 *
 * One row of settings for the whole platform. Two of these fields change what
 * every tenant experiences and are worth naming: `maintenanceMode` makes every
 * endpoint answer `403 MAINTENANCE_MODE`, and `retryAfterDays` is the dunning
 * gap — 3 days, never the same day.
 */
export const platformSettingsResponse = z.object({
  trialDays: z.number().int().min(0).max(365),
  requirePaymentMethod: z.boolean(),
  /** How long a cancelled book's data is kept before purge (design §11: 90). */
  postTrialRetentionDays: z.number().int().min(0).max(3650),
  maxVideoBytes: bytesSchema,
  /** U1 · undecided: keep originals forever, or drop them after N days. */
  keepOriginalVideos: z.boolean(),
  keepOriginalVideoDays: z.number().int().nonnegative().nullish(),
  nightlyBackupHourPkt: z.number().int().min(0).max(23),
  maintenanceMode: z.boolean(),
  maintenanceMessage: z.string().nullish(),
  dunningRetryAfterDays: z.number().int().min(1).max(30),
  gateways: z.array(
    z.object({
      method: invoiceMethodSchema,
      businesses: z.number().int().nonnegative(),
      /** `MANUAL` in v1 for all of them — no gateway integration exists (D3). */
      mode: z.enum(['LIVE', 'MANUAL']),
    }),
  ),
})

export const patchPlatformSettingsRequest = z
  .object({
    trialDays: z.number().int().min(0).max(365).optional(),
    requirePaymentMethod: z.boolean().optional(),
    postTrialRetentionDays: z.number().int().min(0).max(3650).optional(),
    maxVideoBytes: bytesSchema.optional(),
    keepOriginalVideos: z.boolean().optional(),
    keepOriginalVideoDays: z.number().int().nonnegative().nullish(),
    nightlyBackupHourPkt: z.number().int().min(0).max(23).optional(),
    maintenanceMode: z.boolean().optional(),
    maintenanceMessage: z.string().trim().max(500).nullish(),
    dunningRetryAfterDays: z.number().int().min(1).max(30).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, 'Nothing to change.')

/* ── P18 · GET/POST /platform/team ────────────────────────────────────────── */

/**
 * Platform users have `tenant_id IS NULL` and their own role union — a tenant
 * `OWNER` and a `PLATFORM_OWNER` are deliberately not the same type anywhere in
 * this codebase, because merging them is exactly the mistake that would hand a
 * trader the platform console.
 */
export const teamMemberSchema = z.object({
  id: idSchema,
  name: z.string(),
  username: usernameSchema,
  role: platformRoleSchema,
  isActive: z.boolean(),
  booksOpened: z.number().int().nonnegative(),
  lastSignedInAt: isoInstantSchema.nullish(),
})

export const createTeamMemberRequest = z.object({
  name: z.string().trim().min(2).max(120),
  username: usernameSchema,
  role: platformRoleSchema,
  password: passwordSchema.optional(),
})

export const createTeamMemberResponse = z.object({
  member: teamMemberSchema,
  oneTimePassword: z.string().min(8).optional(),
})

export const teamListResponse = z.array(teamMemberSchema)

/* ── P19 · POST /platform/broadcast ───────────────────────────────────────── */

/**
 * "Message every owner." **G22 · no provider is decided**, so this contract
 * describes the request and the queue receipt only — the response says what was
 * accepted, never that anything was delivered. Nothing here should be read as
 * committing to a channel.
 */
export const broadcastRequest = z.object({
  channel: z.enum(['WHATSAPP', 'SMS', 'EMAIL', 'IN_APP']),
  subject: z.string().trim().min(3).max(200),
  body: z.string().trim().min(3).max(5000),
  /** Who gets it. Omitted filters mean "every active book". */
  audience: z
    .object({
      status: tenantStatusSchema.optional(),
      planCode: planCodeSchema.optional(),
      nearLimit: z.boolean().optional(),
    })
    .default({}),
  note: noteSchema.optional(),
})

export const broadcastResponse = z.object({
  broadcastId: idSchema,
  queued: z.number().int().nonnegative(),
  /** Sending is asynchronous and unimplemented; this is a receipt, not a result. */
  status: z.enum(['QUEUED']),
})

export type PlatformSettingsResponse = z.infer<typeof platformSettingsResponse>
export type PatchPlatformSettingsRequest = z.input<typeof patchPlatformSettingsRequest>
export type TeamMember = z.infer<typeof teamMemberSchema>
export type CreateTeamMemberRequest = z.input<typeof createTeamMemberRequest>
export type BroadcastRequest = z.input<typeof broadcastRequest>
export type BroadcastResponse = z.infer<typeof broadcastResponse>
